import React from 'react';
import { useSearchParams } from 'react-router-dom';
import { JobType, LoanStatus } from '../graphql/types';
import type { Document, Loan } from '../graphql/types';
import type { PillTone } from '../components/ui/Pill';
import { StatusDot } from '../components/ui/StatusDot';
import type { StatusDotTone } from '../components/ui/StatusDot';
import type { AnalyzeResult } from './analyze/analyzeModel';
import { RailItem } from './Rail';
import {
  formatConfirmed,
  getLoanRailStatus,
  loanConfirmationsFromSaved,
  loanRailNote,
} from './railStatus';
import type { ConfirmationSummary } from './railStatus';
import { readWorkspaceParams, writeWorkspaceParams } from './urlState';

export { formatConfirmed };
export type { ConfirmationSummary };

/**
 * LoanRail (DD): the loans in the job, shown inside the Workspace Rail.
 *
 * The heading carries a count note ("Loans · 3 need you"). Each row shows the
 * loan number, the borrower, and one plain-language status line next to a
 * status dot ("Blocked · missing Final HUD-1", "Name conflict across docs",
 * "Guideline review pending", "All checks passed", "Low-confidence field").
 * The line is derived from the stored findings and the low-confidence field
 * counts (`getLoanRailStatus`). When known, "n of m confirmed" follows.
 *
 * Confirmations come from the `confirmations` prop, or, when `jobId` and
 * `documentsByLoan` are given, from the confirmations saved for each loan.
 *
 * Selecting a loan writes `loan` to the URL and clears the selections that
 * belong to the previous loan (`doc`, `field`, `exception`). `mode` and
 * unrelated params are kept, so the selection survives Mode changes
 * (Requirement 3.4).
 *
 * Requirement: 3.1.
 */
export interface LoanRailProps {
  loans: ReadonlyArray<Loan>;
  /** Stored completeness, consistency and guideline findings for the job. */
  analysis?: AnalyzeResult | null;
  /** Fields read with low confidence, per loan id. */
  lowConfidenceCounts?: Readonly<Record<string, number | undefined>>;
  /** Field confirmations per loan id. Overrides the saved confirmations. */
  confirmations?: Readonly<Record<string, ConfirmationSummary | undefined>>;
  /** With `documentsByLoan`, reads "n of m confirmed" from the saved confirmations. */
  jobId?: string;
  documentsByLoan?: Readonly<Record<string, ReadonlyArray<Document>>>;
  /** Overrides the `loan` URL param as the selected loan. */
  selectedLoanId?: string | null;
  /** Called after a loan is chosen, in addition to the URL update. */
  onSelect?: (loanId: string) => void;
  /** Extra rows between the heading and the loans, for example "All loans" in Analyze. */
  leading?: React.ReactNode;
}

export interface RailStatusDisplay {
  label: string;
  tone: PillTone;
  dot: StatusDotTone;
}

export function getLoanStatusDisplay(loan: Pick<Loan, 'status' | 'failedFileCount'>): RailStatusDisplay {
  if (loan.failedFileCount > 0) {
    return { label: `${loan.failedFileCount} failed`, tone: 'danger', dot: 'danger' };
  }
  switch (loan.status) {
    case LoanStatus.PROCESSING:
      return { label: 'Extracting', tone: 'selected', dot: 'running' };
    case LoanStatus.REVIEW_REQUIRED:
      return { label: 'Needs review', tone: 'warning', dot: 'warning' };
    case LoanStatus.REVIEW_COMPLETED:
      return { label: 'Reviewed', tone: 'success', dot: 'success' };
    default:
      return { label: 'Not started', tone: 'neutral', dot: 'idle' };
  }
}

export function getLoanTitle(loan: Pick<Loan, 'loanNumber' | 'borrowerName' | 'loanId'>): string {
  return loan.loanNumber?.trim() || loan.borrowerName?.trim() || loan.loanId;
}

export function formatFileCount(count: number): string {
  return `${count} ${count === 1 ? 'file' : 'files'}`;
}

export const RAIL_HEADING_CLASS =
  'flex items-baseline justify-between gap-2 px-4 pb-2 pt-4 text-[11px] font-bold uppercase tracking-[0.1em] text-gray-500';

/** Count note beside the heading, in the primary-deep violet. */
export const RAIL_NOTE_CLASS = 'text-xs font-bold normal-case tracking-normal text-[#6E2EF0]';

export const LoanRail: React.FC<LoanRailProps> = ({
  loans,
  analysis,
  lowConfidenceCounts,
  confirmations,
  jobId,
  documentsByLoan,
  selectedLoanId,
  onSelect,
  leading,
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const selected =
    selectedLoanId !== undefined ? selectedLoanId : readWorkspaceParams(searchParams, JobType.DD).loan;

  const statuses = loans.map((loan) =>
    getLoanRailStatus({
      loan,
      analysis,
      lowConfidenceCount: lowConfidenceCounts?.[loan.loanId] ?? 0,
    })
  );
  const note = loanRailNote(statuses);

  // Saved confirmations are read on every render so a confirm made in Extract shows at once.
  const saved =
    !confirmations && jobId && documentsByLoan
      ? loanConfirmationsFromSaved(jobId, documentsByLoan)
      : undefined;
  const summaries = confirmations ?? saved;

  const choose = (loanId: string) => {
    if (loanId !== selected) {
      setSearchParams(
        writeWorkspaceParams(searchParams, { loan: loanId, doc: null, field: null, exception: null })
      );
    }
    onSelect?.(loanId);
  };

  return (
    <div data-testid="loan-rail">
      <p className={RAIL_HEADING_CLASS}>
        <span>Loans</span>
        {note && (
          <span className={RAIL_NOTE_CLASS} data-testid="rail-note">
            {note}
          </span>
        )}
      </p>
      {leading}
      {loans.length === 0 ? (
        <p className="px-4 py-3 text-sm text-gray-500">
          No loans yet. Add loans in Intake to see them here.
        </p>
      ) : (
        loans.map((loan, index) => {
          const status = statuses[index];
          const summary = summaries?.[loan.loanId];
          const title = getLoanTitle(loan);
          const borrower = loan.borrowerName?.trim();
          return (
            <RailItem
              key={loan.loanId}
              selected={loan.loanId === selected}
              onClick={() => choose(loan.loanId)}
            >
              <span className="flex items-center justify-between gap-2">
                <span className="min-w-0 flex-1">
                  <span className="block truncate font-bold text-gray-900">{title}</span>
                  {borrower && borrower !== title && (
                    <span className="mt-0.5 block truncate text-xs text-gray-500">{borrower}</span>
                  )}
                  <span className="mt-0.5 block text-xs text-gray-700" data-testid="loan-status">
                    {status.line}
                  </span>
                  {summary && summary.total > 0 && (
                    <span className="mt-0.5 block text-xs text-gray-500" data-testid="loan-confirmed">
                      {formatConfirmed(summary)}
                    </span>
                  )}
                </span>
                <StatusDot tone={status.dot} size={10} label={status.line} />
              </span>
            </RailItem>
          );
        })
      )}
    </div>
  );
};

export default LoanRail;
